import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema, SITE_URL } from "@/lib/seo";

const PATH = "/fragen/wie-lange-dauert-ein-shopify-shop";
const TITLE = "Wie lange dauert es, einen Shopify-Shop zu erstellen?";
const DESCRIPTION =
  "Realistische Zeitrahmen für einen Shopify-Shop: von der Einrichtung mit fertigem Theme bis zum Projekt mit Schnittstellen, und was den Start am häufigsten verzögert.";
const UPDATED = "2026-09-10";
const ANSWER =
  "Ein überschaubarer Shop auf Basis eines soliden Themes ist in der Regel in zwei bis vier Wochen startklar. Kommen viele Varianten, mehrere Sprachen, B2B-Preise oder Schnittstellen dazu, werden daraus sechs bis zwölf Wochen. Der häufigste Verzögerungsgrund sind nicht Technik oder Design, sondern fehlende Produktdaten, Bilder und Texte.";

const faqs = [
  {
    q: "Wie lange dauert ein einfacher Shopify-Shop?",
    a: "Mit einem soliden Theme, vorhandenen Produktdaten und klarem Sortiment sind zwei bis vier Wochen bis zum Start üblich.",
  },
  {
    q: "Was verlängert die Umsetzung?",
    a: "Viele Varianten, mehrere Sprachen oder Länder, B2B-Preise, Schnittstellen zu Warenwirtschaft oder Versand und individuelle Funktionen. Ebenso Abstimmungsschleifen ohne klare Entscheidung.",
  },
  {
    q: "Woran scheitert der Zeitplan am häufigsten?",
    a: "An Inhalten. Produktdaten, Bilder, Texte, Versand- und Rechtstexte kommen später als geplant. Wer diese Dinge früh bereitstellt, verkürzt das Projekt am stärksten.",
  },
  {
    q: "Kann ein Shop schneller live gehen?",
    a: "Ja, wenn Sie mit einem kleinen Sortiment starten und Sonderwünsche nach dem Livegang stundenweise nachziehen. Das bringt früher echte Daten statt Annahmen.",
  },
  {
    q: "Wie lange dauert ein Umzug von einem anderen System?",
    a: "Das hängt an Datenmenge und Weiterleitungsplan. Der Aufbau selbst ist meist nicht das Problem, sondern die saubere Übernahme von Produkten, Kunden und URLs.",
  },
];

export const Route = createFileRoute("/fragen/wie-lange-dauert-ein-shopify-shop")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Projektdauer",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({ question: TITLE, answer: ANSWER, path: PATH, updated: UPDATED }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Dauer"
      title="Wie lange dauert ein Shopify-Shop?"
      intro="Kurz gesagt: zwei bis vier Wochen für einen klar umrissenen Shop, sechs bis zwölf Wochen, sobald Sonderlogik oder Schnittstellen dazukommen. Der Zeitplan hängt stärker an Ihren Inhalten als an der Technik."
      source="frage-shopify-dauer"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Typische Zeitrahmen nach Umfang",
          p: [
            "Die Spanne entsteht nicht durch Tempo, sondern durch Umfang. Je mehr Sonderfälle abgebildet werden, desto mehr Abstimmung und Test kommt dazu.",
          ],
          list: [
            "Kleiner Shop, solides Theme, Produktdaten vorhanden: zwei bis vier Wochen",
            "Mittleres Projekt mit Theme-Anpassungen und mehreren Kategorien: vier bis acht Wochen",
            "Mehrere Sprachen, B2B-Preise oder Schnittstellen: sechs bis zwölf Wochen",
            "Individuelle Funktionen und Konfiguratoren: nach Aufwand, stundenweise geplant",
          ],
        },
        {
          h: "Was die Zeit tatsächlich frisst",
          p: [
            "Nicht das Aufsetzen des Shops, sondern Inhalte und Entscheidungen. Fehlende Produktbilder, unklare Versandregeln und offene Rechtstexte halten Projekte länger auf als jede Entwicklungsaufgabe.",
          ],
          list: [
            "Produktdaten, Varianten und Preise vollständig",
            "Bilder in einheitlicher Qualität",
            "Versand- und Zahlungsregeln entschieden",
            "Impressum, Datenschutz, AGB und Widerruf vorhanden",
            "Eine Person auf Ihrer Seite, die entscheidet",
          ],
        },
        {
          h: "So wird es schneller",
          p: [
            "Klein starten und früh live gehen. Ein Shop mit dem Kernsortiment bringt nach wenigen Wochen echte Daten. Was danach wirklich fehlt, lässt sich gezielt nachziehen, statt es vorab zu erraten.",
            "Für Anpassungen und Entwicklung rechne ich mit 110 € netto pro Stunde ab, für den Aufbau nach Umfang mit Festpreis.",
          ],
        },
        {
          h: "Ablauf in der Praxis",
          p: [],
          list: [
            "Gespräch und Umfang klären, Angebot schriftlich",
            "Theme aufsetzen, Struktur und Navigation",
            "Produktdaten, Zahlungen, Versand, Rechtstexte",
            "Testbestellungen und Korrekturen",
            "Livegang, danach Anpassungen nach Bedarf",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/fragen/was-kostet-ein-shopify-shop", label: "Was kostet ein Shopify-Shop?" },
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration" },
      ]}
    />
  );
}
